import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  currentStepAtom,
  isCompleteAtom,
  incrementStepAtom,
  initializeProgressBarAtom,
} from '../context/progressBarAtoms';
import { Step } from '../components/StepsList/StepsList';
import { LOADER_COMPLETE_DELAY, LOADER_STEP_TIME } from '../../../consts';

interface UseProgressTimerOptions {
  steps: Step[];
  background?: string;
  isLoading?: boolean;
  onComplete?: () => void;
}

export function useProgressTimer({
  steps,
  background,
  isLoading = false,
  onComplete,
}: UseProgressTimerOptions) {
  const currentStep = useAtomValue(currentStepAtom);
  const isComplete = useAtomValue(isCompleteAtom);
  const incrementStep = useSetAtom(incrementStepAtom);
  const initializeProgressBar = useSetAtom(initializeProgressBarAtom);

  // Initialize on mount or when steps change
  useEffect(() => {
    if (!steps.length) {
      return;
    }
    initializeProgressBar({ steps, background });
  }, [steps, background, initializeProgressBar]);

  // Progress increment loop
  useEffect(() => {
    if (!steps.length || isComplete) {
      return;
    }

    const timer = setTimeout(incrementStep, LOADER_STEP_TIME);
    return () => clearTimeout(timer);
  }, [steps.length, currentStep, isComplete, incrementStep]);

  // Completion callback - only fires when BOTH:
  // 1. Progress bar is complete (isComplete)
  // 2. External loading is done (!isLoading)
  useEffect(() => {
    if (!isComplete || isLoading || !onComplete) {
      return;
    }

    const timer = setTimeout(onComplete, LOADER_COMPLETE_DELAY);
    return () => clearTimeout(timer);
  }, [isComplete, isLoading, onComplete]);

  return { currentStep, isComplete };
}
